import { Controller } from "@hotwired/stimulus"

// FD-427: render Mermaid diagrams inside a rendered Markdown page.
//
// MarkdownRenderer turns ```mermaid fences into <pre class="mermaid"> holding the
// raw diagram source; this controller (attached to the .fd-prose container only
// when the page is markdown) finds those blocks and renders them.
//
// Mermaid is loaded with a dynamic import() so its heavy code only downloads,
// parses and inits when a page actually has diagrams. Since FD-483 the build
// runs with --splitting, so this import is a real on-demand chunk under
// app/assets/builds/chunks/ (~6 MB of mermaid/cytoscape/katex source stays out
// of application.js). Chunks are same-origin (CSP script-src 'self', no CDN)
// and bin/sync-main copies the whole builds dir — chunks included — to the dev box.
export default class extends Controller {
  async connect() {
    const blocks = Array.from(this.element.querySelectorAll("pre.mermaid"))
    if (blocks.length === 0) return

    const { default: mermaid } = await import("mermaid")
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: "strict", // mermaid sanitizes diagram-generated HTML
      theme: this.#dark ? "dark" : "default",
      fontFamily: "Inter, sans-serif",
      // mermaid 12 defaults to the ELK layout and the "neo" look. Keep 11's dagre layout and
      // classic look so existing diagrams render as they did, and so a flowchart does not
      // download the 1.5 MB ELK chunk (FD-938).
      layout: "dagre",
      look: "classic",
      // On a parse error mermaid.render draws a "Syntax error" diagram into a temporary
      // node on <body> before it throws; don't draw it (the source stays visible instead).
      suppressErrorRendering: true,
    })

    // Render each block in order. On a parse error, leave the source visible
    // (as a code block) rather than blanking the page.
    for (let i = 0; i < blocks.length; i++) {
      const pre = blocks[i]
      const source = pre.textContent
      const id = `fd-mermaid-${this.#uid}-${i}`
      try {
        const { svg } = await mermaid.render(id, source)
        const figure = document.createElement("figure")
        figure.className = "fd-mermaid"
        figure.innerHTML = svg
        pre.replaceWith(figure)
      } catch (e) {
        // …and drop the temporary render nodes if it left any behind.
        document.querySelectorAll(`#d${id}, #i${id}`).forEach((node) => node.remove())
        pre.classList.add("fd-mermaid-error")
        pre.classList.remove("mermaid")
      }
    }
  }

  get #dark() {
    return document.documentElement.getAttribute("data-theme") === "dark"
  }

  get #uid() {
    this._uid ||= Math.random().toString(36).slice(2, 8)
    return this._uid
  }
}
